import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as Sentry from '@sentry/react';
import i18n from '@/i18n/config';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class GlobalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Global Error Boundary caught:', error, errorInfo);

    // Report to Sentry
    try {
      Sentry.withScope((scope) => {
        scope.setTag('boundary', 'global');
        scope.setExtra('componentStack', errorInfo.componentStack);
        Sentry.captureException(error);
      });
    } catch {
      // Sentry may not be initialized
    }

    // Report crash to Edge Function (Slack + admin notifications)
    this.reportCrash(error);
  }

  /** Stale-deploy chunk misses are not real crashes: the visitor is holding an
   *  old index.html whose hashed chunks no longer exist. They self-heal on
   *  reload, so they must never page the team. */
  private static isStaleChunkError(error: Error): boolean {
    const msg = `${error?.message ?? ""}`;
    return (
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg) ||
      /ChunkLoadError/i.test(`${error?.name ?? ""}`)
    );
  }

  private async reportCrash(error: Error) {
    try {
      if (GlobalErrorBoundary.isStaleChunkError(error)) return;
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.functions.invoke('kb-ops/fn/report-crash', {
        body: {
          error_message: error.message,
          error_stack: error.stack,
          url: window.location.href,
          user_agent: navigator.userAgent,
          user_id: user?.id || null,
          timestamp: new Date().toISOString(),
        },
      });
    } catch {
      // Crash reporting itself should never throw
    }
  }


  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private t(key: string, fallback: string): string {
    try {
      const val = i18n.t(key);
      return val && val !== key ? val : fallback;
    } catch {
      return fallback;
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="w-20 h-20 bg-red-50 flex items-center justify-center mx-auto">
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">
                {this.t('errors.globalTitle', 'Something went wrong')}
              </h1>
              <p className="text-muted-foreground">
                {this.t('errors.globalMessage', "We're sorry, but something unexpected happened. The error has been logged and we'll look into it.")}
              </p>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="text-left bg-muted p-4 rounded-lg text-sm">
                <summary className="cursor-pointer font-medium mb-2">Error Details</summary>
                <div className="space-y-2 font-mono text-xs">
                  <div>
                    <strong>Error:</strong> {this.state.error.message}
                  </div>
                  {this.state.error.stack && (
                    <div>
                      <strong>Stack:</strong>
                      <pre className="whitespace-pre-wrap mt-1 text-xs">
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleRetry} className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                {this.t('errors.tryAgain', 'Try Again')}
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/'}
              >
                {this.t('errors.goHome', 'Go Home')}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              {this.t('errors.persistMessage', 'If this problem persists, please contact our support team.')}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
